import {inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  UrlSegment,
  UrlTree,
  ViewTransitionInfo
} from '@angular/router';

export interface ViewTransitionNavigationContext {
  current: UrlSegment[];
  previous: UrlSegment[];
}

export const onViewTransitionCreated = (info: ViewTransitionInfo) => {
  const service = inject(ViewTransitionService);
  service.setCurrentTransition(info);

  info.transition.finished.finally(() => {
    service.clearTransitionState();
  });
};

@Injectable({ providedIn: 'root' })
export class ViewTransitionService {
  readonly currentTransition = signal<ViewTransitionInfo | undefined>(undefined);
  readonly navigationContext = signal<ViewTransitionNavigationContext | undefined>(undefined);

  private readonly router = inject(Router);
  private lastSuccessfulUrlTree: UrlTree;

  constructor() {
    this.lastSuccessfulUrlTree = this.router.parseUrl(this.router.url || '/');

    this.router.events
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          const currentTree = this.router.parseUrl(event.url);
          this.navigationContext.set({
            current: this.extractPrimarySegments(currentTree),
            previous: this.extractPrimarySegments(this.lastSuccessfulUrlTree)
          });
          return;
        }

        if (event instanceof NavigationEnd) {
          this.lastSuccessfulUrlTree = this.router.parseUrl(event.urlAfterRedirects);
          queueMicrotask(() => {
            if (!this.currentTransition()) {
              this.navigationContext.set(undefined);
            }
          });
          return;
        }

        if (event instanceof NavigationCancel || event instanceof NavigationError) {
          this.navigationContext.set(undefined);
        }
      });
  }

  setCurrentTransition(info: ViewTransitionInfo) {
    this.currentTransition.set(info);
  }

  clearTransitionState() {
    this.currentTransition.set(undefined);
    this.navigationContext.set(undefined);
  }

  private extractPrimarySegments(tree: UrlTree | undefined): UrlSegment[] {
    return tree?.root?.children['primary']?.segments ?? [];
  }
}
